import React from 'react';
import { Container, Row, Col, Card, Button, Alert } from 'react-bootstrap';
import { useCart } from '../context/CartContext';
import PayButton from '../components/PayButton';
import { FaTrash, FaMinus, FaPlus, FaShoppingCart } from 'react-icons/fa';

/* ---------------- IMAGE FIX ---------------- */
const getImageUrl = (item) => {
  const img =
    item?.images?.[0] ||
    item?.image ||
    item?.photo ||
    item?.product?.images?.[0] ||
    item?.product?.image ||
    null;

  if (!img) return null;

  if (img.startsWith('http')) return img;

  return `https://apiclientportal.myidealtd.co.uk/uploads/${img}`;
};

const ShoppingCart = () => {

  const {
    cart = [],
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
  } = useCart();

  // ================= USER CHECK =================
  const user = JSON.parse(localStorage.getItem('user'));

  const isLoggedIn = !!user?._id || !!user?.email;

  const isAdmin =
    user?.role === 'admin' ||
    user?.email === 'admin' ||
    user?.email === 'testadmin';

  // ================= FORCE EMPTY CART IF NOT LOGGED IN =================
  const safeCart = isLoggedIn ? cart : [];

  const totalPrice = safeCart.reduce(
    (acc, item) => acc + item.price * item.quantity,
    0
  );

  // ================= ADMIN BLOCK =================
  if (isAdmin) {
    return (
      <Container className="py-5 text-center">
        <Alert variant="danger">
          🚫 Admin users are not allowed to access the shopping cart.
        </Alert>

        <FaShoppingCart size={48} className="mb-3 text-muted" />

        <h3 className="fw-bold">Access Restricted</h3>
        <p className="text-muted">
          This feature is only for customers.
        </p>
      </Container>
    );
  }

  // ================= NOT LOGGED IN =================
  if (!isLoggedIn) {
    return (
      <Container className="py-5 text-center">
        <FaShoppingCart size={48} className="mb-3 text-muted" />

        <h2 className="fw-bold">Your cart is empty</h2>

        <p className="text-muted">
          Please log in to start shopping and save items.
        </p>
      </Container>
    );
  }

  // ================= EMPTY CART =================
  if (safeCart.length === 0) {
    return (
      <Container className="py-5 text-center">
        <FaShoppingCart size={48} className="mb-3 text-muted" />

        <h2 className="fw-bold">Your cart is empty</h2>

        <p className="text-muted">Start shopping now.</p>
      </Container>
    );
  }

  return (
    <>
      <Container className="py-5">
        <h1 className="fw-bold mb-4 text-center">
          Shopping Cart
        </h1>

        <Row className="g-4">

          {/* ================= ITEMS ================= */}
          <Col lg={8}>
            {safeCart.map(item => {

              const imageUrl = getImageUrl(item);

              return (
                <Card
                  key={item._id}
                  className="mb-3 border-0"
                  style={{
                    borderRadius: 16,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)'
                  }}
                >
                  <Card.Body>
                    <Row className="align-items-center g-3">

                      {/* IMAGE */}
                      <Col xs={4} md={3}>
                        <div
                          style={{
                            background: '#f8f9fa',
                            borderRadius: 12,
                            padding: 10,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center'
                          }}
                        >
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={item.name}
                              style={{
                                maxHeight: 80,
                                maxWidth: '100%',
                                objectFit: 'contain'
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                height: 80,
                                width: '100%',
                                background: '#eee',
                                borderRadius: 8
                              }}
                            />
                          )}
                        </div>
                      </Col>

                      {/* DETAILS */}
                      <Col xs={8} md={4}>
                        <h6 className="fw-semibold mb-1">
                          {item.name}
                        </h6>

                        <div className="text-muted">
                          £{item.price.toFixed(2)}
                        </div>
                      </Col>

                      {/* QUANTITY */}
                      <Col xs={6} md={3} className="d-flex align-items-center gap-2">

                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() => decreaseQuantity(item._id)}
                        >
                          <FaMinus />
                        </Button>

                        <span style={{ minWidth: 28, textAlign: 'center', fontWeight: 600 }}>
                          {item.quantity}
                        </span>

                        <Button
                          size="sm"
                          variant="outline-dark"
                          onClick={() => increaseQuantity(item._id)}
                        >
                          <FaPlus />
                        </Button>

                      </Col>

                      {/* TOTAL */}
                      <Col xs={6} md={2} className="text-end">

                        <div className="fw-bold">
                          £{(item.price * item.quantity).toFixed(2)}
                        </div>

                        <Button
                          variant="link"
                          className="text-danger p-0 mt-1"
                          onClick={() => removeFromCart(item._id)}
                        >
                          <FaTrash />
                        </Button>

                      </Col>

                    </Row>
                  </Card.Body>
                </Card>
              );
            })}
          </Col>

          {/* ================= SUMMARY ================= */}
          <Col lg={4} className="d-none d-lg-block">

            <Card
              className="border-0"
              style={{
                borderRadius: 18,
                boxShadow: '0 10px 28px rgba(0,0,0,0.1)'
              }}
            >
              <Card.Body>

                <h5 className="fw-bold mb-3">
                  Order Summary
                </h5>

                <div className="d-flex justify-content-between mb-2">
                  <span>Subtotal</span>
                  <span>£{totalPrice.toFixed(2)}</span>
                </div>

                <div className="d-flex justify-content-between mb-3">
                  <span>Delivery</span>
                  <span className="text-success">Free</span>
                </div>

                <hr />

                <div className="d-flex justify-content-between fs-5 fw-bold mb-4">
                  <span>Total</span>
                  <span>£{totalPrice.toFixed(2)}</span>
                </div>

                <PayButton cart={safeCart} />

              </Card.Body>
            </Card>

          </Col>

        </Row>
      </Container>

      {/* ================= MOBILE SUMMARY ================= */}
      <div
        className="d-lg-none"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          background: '#fff',
          borderTop: '1px solid #dee2e6',
          padding: 16,
          zIndex: 1000
        }}
      >
        <div className="d-flex justify-content-between mb-2">
          <span className="fw-semibold">Total</span>
          <span className="fw-bold fs-5">
            £{totalPrice.toFixed(2)}
          </span>
        </div>

        <PayButton cart={safeCart} />
      </div>
    </>
  );
};

export default ShoppingCart;